import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiMinus } = FiIcons;

const faqData = [
  {
    question: "Was kostet die Zusammenarbeit?",
    answer: [
      "Kostenloses Erstgespräch (ca. 15 Min.): Orientierung, Situations-Check, Optionen aufzeigen (kein Verkaufsgespräch).",
      "Klarheitsanalyse / Erstanalyse (ca. 2 Std.): Paketpreis 350 € – Ziel: Prozesse verstehen, Struktur erkennen, erste Handlungsmöglichkeiten.",
      "Weiterführende Zusammenarbeit: Modular aufgebaut (Analyse, Architektur, Begleitung). Kosten abhängig vom Umfang, Orientierung an üblichen Tagessätzen für System-Architektur/Organisationsberatung.",
      "Förderung: Über INQA-Coaching sind bis zu 80% Zuschuss möglich."
    ]
  },
  {
    question: "Wie funktioniert die Förderung durch INQA-Coaching?",
    answer: [
      "Staatlich gefördertes Programm speziell für KMU mit Fokus auf Beteiligung der Beschäftigten und digitale Arbeitsorganisation.",
      "Umfang: Bis zu 12 Beratungstage sind förderfähig.",
      "Konditionen: Maximal 1.200 € pro Tag förderfähig bei 80 % Zuschuss.",
      "Beispiel: 12 Tage à 1.200 € = 14.400 € Gesamtkosten. 80% Förderung = 11.520 €. Ihr Eigenanteil liegt bei ca. 2.880 €.",
      "Voraussetzung: Klärung der Förderfähigkeit erfolgt unkompliziert im Erstgespräch."
    ]
  },
  {
    question: "Wie läuft die Zusammenarbeit ab?",
    answer: [
      "Schritt 1: kostenloses Erstgespräch (Status Quo).",
      "Schritt 2: Klarheitsanalyse & Bestandsaufnahme.",
      "Schritt 3: Prozessanalyse und Prüfung der Entscheidungslogik.",
      "Schritt 4: Übergabe der System-Architektur & Handlungsempfehlungen.",
      "Besonderheit: Die Tiefe ist individuell wählbar – von Workshops bis zur Begleitung der IT-Umsetzung. Hilfe zur Selbsthilfe steht im Mittelpunkt."
    ]
  },
  {
    question: "Wie viel Zeit muss ich selbst investieren?",
    answer: [
      "Start: 15 Minuten für das Erstgespräch.",
      "Analyse: ca. 2 Stunden für die Klarheitsanalyse.",
      "Projektphase: Abhängig vom Umfang und gewählter Tiefe (z.B. Team-Workshops).",
      "Ziel: Wir arbeiten hocheffizient, damit Ihr Zeitaufwand so gering wie möglich bleibt und der Fokus auf dem Ergebnis liegt."
    ]
  },
  {
    question: "Was bekomme ich als Ergebnis?",
    answer: [
      "Absolute Klarheit über Ihre Kernprozesse.",
      "Eine dokumentierte Entscheidungslogik (Wann passiert was?).",
      "Visuelle System- oder Prozessarchitektur.",
      "Konkrete Handlungsempfehlungen und Prioritäten für die Automatisierung.",
      "Herstellerunabhängigkeit: Sie haben die Grundlage für jede IT-Umsetzung (keine Toolbindung)."
    ]
  },
  {
    question: "Setzen Sie die Lösungen auch um?",
    answer: [
      "Unsere Kernrolle ist die des Architekten (Konzept & Bauplan).",
      "Die Umsetzung erfolgt durch Ihre interne IT, externe Partner Ihrer Wahl oder Sie selbst.",
      "Optionale Begleitung: Wir stehen während der Bauphase beratend zur Seite, um die Einhaltung der Architektur zu sichern.",
      "Ziel: Ihre technologische Unabhängigkeit."
    ]
  },
  {
    question: "Für welche Unternehmen lohnt sich das?",
    answer: [
      "Soloselbstständige mit wachsender Komplexität.",
      "KMU mit historisch gewachsenen, unklaren Prozessen.",
      "Unternehmen mit hohem Anteil an manuellen, repetitiven Tätigkeiten.",
      "Betriebe, die Digitalisierungs- oder KI-Vorhaben planen, aber Fehler durch falsche Architektur vermeiden wollen."
    ]
  }
];

const FAQItem = ({ question, answer, isOpen, onClick }) => {
  return (
    <div className="border-b border-gray-200 last:border-0">
      <button
        onClick={onClick}
        className="w-full py-6 flex items-center justify-between text-left hover:text-azure-600 transition-colors"
      >
        <span className="text-xl font-bold text-black">{question}</span>
        <SafeIcon 
          icon={isOpen ? FiMinus : FiPlus} 
          className={`text-2xl transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="pb-8 space-y-4">
              {answer.map((line, idx) => (
                <div key={idx} className="flex items-start">
                  <div className="w-1.5 h-1.5 bg-azure-600 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                  <p className="text-gray-600 leading-relaxed">{line}</p>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <section id="faq" className="py-24 bg-white">
      <div className="container mx-auto px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-6">Häufige Fragen zur System-Architektur</h2>
            <p className="text-lg text-gray-600">Alles, was Sie über unsere Arbeitsweise, Kosten und Förderung wissen müssen.</p>
          </div>
          <div className="bg-gray-50 rounded-3xl p-4 md:p-8 border border-gray-100">
            {faqData.map((item, index) => (
              <FAQItem
                key={index}
                question={item.question}
                answer={item.answer}
                isOpen={openIndex === index}
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQ;